/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import classNames from 'classnames';
import Loading from 'components/common/loading-component';
import { getThumbnailBySectionId } from 'api/navigation-api';
import { toast } from 'react-toastify';
import ApiErrorNotification, {
  API_ERROR_NOTIFICATION_ID,
} from 'components/util/api-error-notificiation-component';
import CloseToastButton from 'components/util/close-toast-button-component';
import PropTypes from 'prop-types';

const THUMBNAIL_CLASS = 'statement-thumbnail';
const THUMBNAIL_ID = 'statement-thumbnail_id--main';
const THUMBNAIL_ID_LEFT = 'statement-thumbnail_id--left';

class OcrNavigation extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      thumbnails: [],
    };
    this.apiCalls = {};
    this.scrollListener = null;
    this.userClickedThumbnail = false;
    this.scrollTimeout = null;
    this.savedScrollPosition = null;
    this.debounceTimer = null;
    this.thumbnailElementRef = null;
    this.blobUrls = new Set(); // Track created blob URLs for cleanup
  }

  componentDidMount() {
    const { sectionDetails, isLeftSideView } = this.props;
    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;

    // Clear apiCalls on mount to ensure fresh start
    this.apiCalls = {};

    // Cache the thumbnail element reference
    this.thumbnailElementRef = document.getElementById(`${thumbnailID}`);

    // setting number of thumbnails based on number of pages in statement
    this.setState(
      {
        thumbnails:
          sectionDetails &&
          sectionDetails.map((section) => ({
            pageNumber: section.sectionId,
            loaded: false,
            url: null,
            ...section,
          })),
      },
      this.loadThumbnailsInView,
    );

    if (this.thumbnailElementRef) {
      this.scrollListener = this.scrollToChange;
      this.thumbnailElementRef.addEventListener('scroll', this.scrollListener);
    }
  }

  componentWillUnmount() {
    const { isLeftSideView } = this.props;
    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;
    const thumbnailElement = document.getElementById(`${thumbnailID}`);
    if (thumbnailElement && this.scrollListener) {
      thumbnailElement.removeEventListener('scroll', this.scrollListener);
    }
    // Clear any pending scroll timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    // Clear debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    // Revoke all blob URLs to free memory
    this.blobUrls.forEach((url) => URL.revokeObjectURL(url));
    this.blobUrls.clear();
    // Clear apiCalls on unmount
    this.apiCalls = {};
    this.thumbnailElementRef = null;
  }

  componentDidUpdate(prevProps) {
    const { ocrCurrentViewPageNumber, sectionDetails, isLeftSideView } =
      this.props;
    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;

    // Check if isLeftSideView changed - this is the missing condition
    if (prevProps?.isLeftSideView !== isLeftSideView) {
      // Remove listener from previous view
      const prevThumbnailID = prevProps?.isLeftSideView
        ? THUMBNAIL_ID_LEFT
        : THUMBNAIL_ID;
      const prevElement = document.getElementById(`${prevThumbnailID}`);
      if (prevElement && this.scrollListener) {
        prevElement.removeEventListener('scroll', this.scrollListener);
      }

      // Clear apiCalls and reset state when view changes
      this.apiCalls = {};
      this.setState(
        {
          thumbnails:
            sectionDetails &&
            sectionDetails.map((section) => ({
              pageNumber: section?.sectionId,
              loaded: false,
              url: null,
              ...section,
            })),
        },
        this.loadThumbnailsInView,
      );

      // Update cached reference and add listener to new view
      this.thumbnailElementRef = document.getElementById(`${thumbnailID}`);
      if (this.thumbnailElementRef) {
        this.thumbnailElementRef.addEventListener(
          'scroll',
          this.scrollListener,
        );
      }
      return; // Exit early to avoid duplicate processing
    }

    if (
      !this.state.thumbnails.length &&
      prevProps?.sectionDetails &&
      this.props?.sectionDetails &&
      prevProps?.sectionDetails.length !== this.props?.sectionDetails.length
    ) {
      this.setState(
        {
          thumbnails:
            sectionDetails &&
            sectionDetails.map((section) => ({
              ...section,
              pageNumber: section?.sectionId,
              loaded: false,
              url: null,
            })),
        },
        this.loadThumbnailsInView,
      );
    }

    // Clear apiCalls if sectionDetails array reference changed (new data)
    if (prevProps?.sectionDetails !== sectionDetails) {
      this.apiCalls = {};
    }

    if (prevProps?.ocrCurrentViewPageNumber !== ocrCurrentViewPageNumber) {
      // triggered when user scroll the page in central
      // Only auto-scroll if the user didn't manually click a thumbnail
      if (!this.userClickedThumbnail) {
        this.scrollToCurrentThumbNail(ocrCurrentViewPageNumber);
      } else {
        // Restore the scroll position after the thumbnail click
        if (this.thumbnailElementRef && this.savedScrollPosition !== null) {
          this.thumbnailElementRef.scrollTop = this.savedScrollPosition;
        }
      }
      this.userClickedThumbnail = false; // Reset flag
      this.savedScrollPosition = null; // Reset saved position
      this.loadThumbnailsInView();
    }
  }

  // trigger when user scroll in navigation panel
  scrollToChange = () => {
    // Debounce scroll events for performance
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.loadThumbnailsInView();
      this.debounceTimer = null;
    }, 150);
  };

  // scrolling navigational panel based on the central content/page rendered
  scrollToCurrentThumbNail = () => {
    const { ocrCurrentViewPageNumber, isLeftSideView } = this.props;
    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;

    // Clear any existing timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = setTimeout(() => {
      // Check again if user clicked - prevents race conditions
      if (!this.userClickedThumbnail) {
        const element = document.getElementById(
          `${thumbnailID}${ocrCurrentViewPageNumber}`,
        );
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
      this.scrollTimeout = null;
    }, 1700);
  };

  // fetching the thumbnails based on the area available in view with navigational panel
  loadThumbnailsInView = () => {
    const { thumbnails } = this.state;
    const { isLeftSideView } = this.props;

    if (!thumbnails || thumbnails.length === 0) return;

    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;
    const scrollArea =
      this.thumbnailElementRef || document.getElementById(`${thumbnailID}`);

    if (!scrollArea) return; // Guard against missing element

    const scrollAreaBounds = scrollArea.getBoundingClientRect();
    const newThumbnails = [];

    // Optimize loop - only check unloaded thumbnails
    for (let i = 0; i < thumbnails.length; i++) {
      if (thumbnails[i]?.loaded) continue; // Skip already loaded

      const thumbnailPage = document.getElementById(
        `${thumbnailID}${thumbnails[i]?.pageNumber}`,
      );

      if (thumbnailPage) {
        const page = thumbnailPage.getBoundingClientRect();
        if (
          scrollAreaBounds.top < page.top &&
          page.top < scrollAreaBounds.bottom &&
          scrollAreaBounds.top < page.bottom &&
          page.bottom < scrollAreaBounds.bottom
        ) {
          newThumbnails.push(thumbnails[i]?.sectionId);
        }
      }
    }

    if (newThumbnails.length === 0) return; // Early exit if nothing to load

    const promiseThumbnail =
      newThumbnails.length &&
      newThumbnails.map((item) => {
        if (
          !this.apiCalls[item] &&
          !thumbnails.find((section) => section?.sectionId === item)?.loaded
        ) {
          this.apiCalls[item] = getThumbnailBySectionId({
            projectId: this.props.selectedProjectId,
            section: thumbnails.find(
              (section) => section?.sectionId === item && !section?.loaded,
            ),
          }).then((data) => {
            const blobUrl = URL.createObjectURL(new Blob([data]));
            this.blobUrls.add(blobUrl); // Track for cleanup
            return {
              item,
              url: blobUrl,
            };
          });
        }
        return this.apiCalls[item];
      });
    if (newThumbnails.length) {
      Promise.all(promiseThumbnail)
        .then((data) => {
          this.setState({
            thumbnails: thumbnails.map((page) =>
              newThumbnails.includes(page?.sectionId)
                ? {
                    ...page,
                    loaded: true,
                    url: data.find(
                      (response) => response.item === page?.sectionId,
                    )?.url,
                  }
                : page,
            ),
          });
        })
        .catch((error) => {
          toast.error(
            <ApiErrorNotification errResponse={error} shouldSaveError />,
            {
              className: 'Toastify__toast Toastify__toast__no-hover',
              autoClose: false,
              closeOnClick: false,
              closeButton: <CloseToastButton />,
              draggable: false,
              toastId: API_ERROR_NOTIFICATION_ID,
            },
          );
        });
    }
  };
  // Extracted handler for better performance - avoids creating new functions on each render
  handleThumbnailClick = (pageNumber) => {
    this.userClickedThumbnail = true;
    // Clear any pending auto-scroll
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
    // Save current scroll position
    if (this.thumbnailElementRef) {
      this.savedScrollPosition = this.thumbnailElementRef.scrollTop;
    }
    this.props.navigateToSelectedPage(pageNumber);
  };

  handleThumbnailKeyDown = (e, pageNumber) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.handleThumbnailClick(pageNumber);
    }
  };
  // rendering the thumbnail based on the thumbnails fetched/loaded
  renderThumbnails = () => {
    const { ocrCurrentViewPageNumber, isLeftSideView } = this.props;
    const { thumbnails } = this.state;
    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;
    return (
      <>
        {thumbnails.map((item) => {
          return item.loaded ? (
            <div
              className={`${THUMBNAIL_CLASS}__individual`}
              key={item?.sectionId}
              id={`${thumbnailID}${item?.pageNumber}`}
              onClick={() => this.handleThumbnailClick(item?.pageNumber)}
              onKeyDown={(e) =>
                this.handleThumbnailKeyDown(e, item?.pageNumber)
              }
              role="button"
              tabIndex={0}
              aria-label={`Navigate to page ${item?.pageNumber}`}
            >
              <div className={`${THUMBNAIL_CLASS}--individual--image`}>
                <img
                  className={classNames(
                    `${THUMBNAIL_CLASS}__individual--image`,
                    {
                      [`${THUMBNAIL_CLASS}__individual--active`]:
                        item?.pageNumber === ocrCurrentViewPageNumber,
                    },
                  )}
                  id={`${thumbnailID}${item?.pageNumber}__image`}
                  src={item?.url}
                  alt={`thumbnail ${item?.pageNumber}`}
                />
              </div>
              <span className={`${THUMBNAIL_CLASS}__individual--caption`}>
                {item?.pageNumber}
              </span>
            </div>
          ) : (
            <div
              className={classNames(`${THUMBNAIL_CLASS}__individual`, {
                [`${THUMBNAIL_CLASS}__individual--active`]:
                  item?.pageNumber === ocrCurrentViewPageNumber,
              })}
              key={item?.sectionId}
              id={`${thumbnailID}${item?.pageNumber}`}
              onClick={() => this.handleThumbnailClick(item?.pageNumber)}
              onKeyDown={(e) =>
                this.handleThumbnailKeyDown(e, item?.pageNumber)
              }
              role="button"
              tabIndex={0}
              aria-label={`Navigate to page ${item?.pageNumber}`}
            >
              <div className={`${THUMBNAIL_CLASS}--individual--image`}>
                <Loading />
              </div>
              <span className={`${THUMBNAIL_CLASS}__individual--caption`}>
                {item?.pageNumber}
              </span>
            </div>
          );
        })}
      </>
    );
  };

  render() {
    const { isLeftSideView } = this.props;
    const thumbnailID = isLeftSideView ? THUMBNAIL_ID_LEFT : THUMBNAIL_ID;

    return (
      <div className={`${THUMBNAIL_CLASS}`} id={`${thumbnailID}`}>
        {this.renderThumbnails()}
      </div>
    );
  }
}

OcrNavigation.propTypes = {
  // sectionDetails is expected to be an array of objects, each containing at least a sectionId property
  sectionDetails: PropTypes.array.isRequired,
  // navigateToSelectedPage is a function that takes a page number as an argument and navigates to that page
  navigateToSelectedPage: PropTypes.func.isRequired,
  // selectedProjectId is a string that identifies the current project, used for API calls to fetch thumbnails
  selectedProjectId: PropTypes.string.isRequired,
  // ocrCurrentViewPageNumber is a number that indicates the currently viewed page in the central content area, used to highlight the corresponding thumbnail and auto-scroll the navigation panel
  ocrCurrentViewPageNumber: PropTypes.number.isRequired,
  // isLeftSideView is a boolean that indicates whether the navigation panel is on the left side, used to determine which thumbnail container to target for scrolling and event listeners
  isLeftSideView: PropTypes.bool,
};

export default OcrNavigation;
